import { asId, formatChatTime } from '../travelChatModel';
import { decisionTypeLabel } from '../../journey-decision/journeyDecisionModel';
import { formatMoneyDisplay, formatSignedMoneyDisplay, moneyToCanonicalString, moneyToCanonicalStringSigned, netBalanceKind, netBalanceLabel } from '../../finance/financeMoney';
import { expenseStatusLabel } from '../../journey-expense/journeyExpenseModel';
import {
  activityStatusProductLabel,
  activityTypeProductLabel,
} from '../../journey-planning/planningPresentation';

export const CARD_SCHEMA_V1 = 1;

export const DECISION_CARD = 'DECISION_CARD';
export const SITUATION_CARD = 'SITUATION_CARD';
export const BUSINESS_CARD = 'BUSINESS_CARD';
export const ACTIVITY_RENDERER_KEY = 'travel.activity';
export const MEMORY_RENDERER_KEY = 'travel.memory';
export const EXPENSE_RENDERER_KEY = 'travel.expense';
export const BALANCE_RENDERER_KEY = 'travel.balance';

const DECISION_STATUS_COPY = Object.freeze({
  DRAFT: '草稿',
  OPEN: '開放投票',
  CLOSED: '已決議',
  CANCELLED: '已取消',
});

const SITUATION_STATUS_COPY = Object.freeze({
  OPEN: '進行中',
  RESOLVED: '已排除',
  CANCELLED: '已取消',
});

const SITUATION_SEVERITY_COPY = Object.freeze({
  INFO: '資訊',
  LOW: '低',
  MEDIUM: '中',
  HIGH: '高',
  CRITICAL: '緊急',
});

const SITUATION_TYPE_COPY = Object.freeze({
  FLIGHT_DELAY: '航班延誤',
  FLIGHT_CANCELLED: '航班取消',
  ACCOMMODATION: '住宿',
  RESTAURANT: '餐廳',
  TRANSPORT: '交通',
  WEATHER: '天氣',
  HEALTH: '健康',
  LOST_ITEM: '遺失物品',
  MEETING: '集合',
  SAFETY: '安全',
  EMERGENCY: '緊急',
  OTHER: '其他',
});

export function isSnowflakeId(value) {
  return /^[1-9]\d*$/.test(asId(value));
}

export function cardSchemaVersion(snapshot) {
  if (snapshot == null || snapshot.cardSchemaVersion == null || snapshot.cardSchemaVersion === '') {
    return CARD_SCHEMA_V1;
  }
  const raw = Number(snapshot.cardSchemaVersion);
  return Number.isInteger(raw) ? raw : Number.NaN;
}

export function isSupportedCardSchema(snapshot) {
  return cardSchemaVersion(snapshot) === CARD_SCHEMA_V1;
}

export function decisionWebRoute(snapshot) {
  const journeyId = asId(snapshot?.journeyId);
  const decisionId = asId(snapshot?.decisionId);
  if (!isSnowflakeId(journeyId) || !isSnowflakeId(decisionId)) return '';
  return `/journeys/${journeyId}/decisions/${decisionId}`;
}

export function situationWebRoute(snapshot) {
  const journeyId = asId(snapshot?.journeyId);
  const situationId = asId(snapshot?.situationId);
  if (!isSnowflakeId(journeyId) || !isSnowflakeId(situationId)) return '';
  return `/journeys/${journeyId}/situations/${situationId}`;
}

export function decisionStatusLabel(status) {
  const key = String(status || '');
  if (DECISION_STATUS_COPY[key]) return DECISION_STATUS_COPY[key];
  if (!key) return '未知狀態';
  return `未知狀態（${key}）`;
}

export function situationStatusLabel(status) {
  const key = String(status || '');
  if (SITUATION_STATUS_COPY[key]) return SITUATION_STATUS_COPY[key];
  if (!key) return '未知狀態';
  return `未知狀態（${key}）`;
}

export function situationSeverityLabel(severity) {
  const key = String(severity || '');
  return SITUATION_SEVERITY_COPY[key] || key;
}

export function situationTypeLabel(type) {
  const key = String(type || '');
  return SITUATION_TYPE_COPY[key] || key;
}

export function formatCardDeadline(value) {
  return formatChatTime(value);
}

export function cardFallbackText(message, unsupportedCopy) {
  const text = String(message?.text || '').trim();
  if (text) return text;
  return unsupportedCopy;
}

export function normalizeDecisionCard(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    ...raw,
    cardSchemaVersion: raw.cardSchemaVersion == null ? null : Number(raw.cardSchemaVersion),
    decisionId: asId(raw.decisionId),
    journeyId: asId(raw.journeyId),
    title: String(raw.title || '').trim(),
    decisionType: String(raw.decisionType || ''),
    decisionStatus: String(raw.decisionStatus || ''),
    deepLink: String(raw.deepLink || ''),
    deadlineAt: raw.deadlineAt || null,
    decisionTypeLabel: raw.decisionType ? decisionTypeLabel(raw.decisionType) : '',
    decisionStatusLabel: decisionStatusLabel(raw.decisionStatus),
    webRoute: decisionWebRoute(raw),
  };
}

export function activityWebRoute(payload) {
  const journeyId = asId(payload?.journeyId);
  const activityId = asId(payload?.activityId);
  if (!isSnowflakeId(journeyId) || !isSnowflakeId(activityId)) return '';
  return `/journeys/${journeyId}/planning?activity=${activityId}`;
}

export function activityTypeLabel(type) {
  return activityTypeProductLabel(type);
}

export function activityStatusLabel(status) {
  return activityStatusProductLabel(status) || '';
}

export function formatActivityTimeRange(start, end) {
  const a = String(start || '').replace(/:00$/, '').slice(0, 5);
  const b = String(end || '').replace(/:00$/, '').slice(0, 5);
  if (a && b) return `${a} – ${b}`;
  return a || b || '';
}

export function normalizeActivityBusinessCard(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const payload = raw.payload && typeof raw.payload === 'object' ? raw.payload : {};
  return {
    rendererKey: String(raw.rendererKey || ''),
    cardSchemaVersion: raw.cardSchemaVersion == null ? null : Number(raw.cardSchemaVersion),
    fallbackText: String(raw.fallbackText || '').trim(),
    payload,
    activityId: asId(payload.activityId),
    journeyId: asId(payload.journeyId),
    title: String(payload.title || raw.fallbackText || '').trim(),
    activityType: String(payload.activityType || ''),
    activityTypeLabel: payload.activityType ? activityTypeLabel(payload.activityType) : '',
    plannedStartTime: payload.plannedStartTime || '',
    plannedEndTime: payload.plannedEndTime || '',
    status: String(payload.status || ''),
    statusLabel: activityStatusLabel(payload.status),
    locationLabel: String(payload.locationLabel || '').trim(),
    webRoute: activityWebRoute(payload),
  };
}

export function expenseWebRoute(payload) {
  const journeyId = asId(payload?.journeyId);
  const expenseId = asId(payload?.expenseId);
  if (!isSnowflakeId(journeyId) || !isSnowflakeId(expenseId)) return '';
  return `/journeys/${journeyId}/expenses?expense=${expenseId}`;
}

const EXPENSE_SPLIT_COPY = Object.freeze({
  EQUAL: '平均分攤',
  EXACT_AMOUNT: '指定金額',
  PERCENTAGE: '比例',
  SHARES: '份數',
  PAYER_ONLY: '付款人自付',
});

export function expenseSplitMethodLabel(method) {
  const key = String(method || '');
  return EXPENSE_SPLIT_COPY[key] || '';
}

export function formatExpenseCardAmount(amount, currencyCode) {
  const currency = String(currencyCode || '').trim().toUpperCase();
  if (!currency) return '';
  if (amount == null || amount === '') return '';
  const canonical = moneyToCanonicalString(amount);
  if (!canonical) return '';
  return `${formatMoneyDisplay(canonical)} ${currency}`;
}

export function normalizeExpenseBusinessCard(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const payload = raw.payload && typeof raw.payload === 'object' ? raw.payload : {};
  const amount = payload.amount == null ? '' : String(payload.amount);
  const currencyCode = String(payload.currencyCode || '').toUpperCase();
  return {
    rendererKey: String(raw.rendererKey || ''),
    cardSchemaVersion: raw.cardSchemaVersion == null ? null : Number(raw.cardSchemaVersion),
    fallbackText: String(raw.fallbackText || '').trim(),
    payload,
    expenseId: asId(payload.expenseId),
    journeyId: asId(payload.journeyId),
    title: String(payload.title || raw.fallbackText || '').trim(),
    notePreview: String(payload.notePreview || '').trim(),
    categoryCode: String(payload.categoryCode || ''),
    amount,
    currencyCode,
    amountLabel: formatExpenseCardAmount(amount, currencyCode),
    payerMemberId: asId(payload.payerMemberId),
    occurredAt: payload.occurredAt || '',
    status: String(payload.status || ''),
    statusLabel: payload.status ? expenseStatusLabel(payload.status) : '',
    splitMethod: String(payload.splitMethod || ''),
    splitMethodLabel: expenseSplitMethodLabel(payload.splitMethod),
    participantCount: Number.isFinite(Number(payload.participantCount))
      ? Number(payload.participantCount)
      : null,
    webRoute: expenseWebRoute(payload),
  };
}

export function balanceWebRoute(payload) {
  const journeyId = asId(payload?.journeyId);
  if (!isSnowflakeId(journeyId)) return '';
  return `/journeys/${journeyId}/split`;
}

export function calculationStatusLabel(status) {
  const key = String(status || '');
  if (key === 'CURRENT') return '依目前支出計算';
  if (key === 'OUTDATED') return '依上次分帳計算（支出已變更）';
  return key ? `未知狀態（${key}）` : '';
}

export function normalizeBalanceBusinessCard(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const payload = raw.payload && typeof raw.payload === 'object' ? raw.payload : {};
  const currencyCode = String(payload.baseCurrency || payload.currencyCode || '').toUpperCase();
  const total = payload.totalActiveExpenseAmount == null ? '' : String(payload.totalActiveExpenseAmount);
  const members = Array.isArray(payload.members)
    ? payload.members.map((row) => {
      const netAmount = moneyToCanonicalStringSigned(row?.netAmount) || '0.00000000';
      const kind = netBalanceKind(netAmount);
      return {
        memberId: asId(row?.memberId),
        paidAmount: moneyToCanonicalString(row?.paidAmount) || '0.00000000',
        owedAmount: moneyToCanonicalString(row?.owedAmount) || '0.00000000',
        netAmount,
        currencyCode: String(row?.currencyCode || currencyCode).toUpperCase(),
        kind,
        kindLabel: netBalanceLabel(kind),
        netLabel: formatSignedMoneyDisplay(netAmount),
      };
    }).filter((row) => row.memberId)
    : [];
  return {
    rendererKey: String(raw.rendererKey || ''),
    cardSchemaVersion: raw.cardSchemaVersion == null ? null : Number(raw.cardSchemaVersion),
    fallbackText: String(raw.fallbackText || '').trim(),
    payload,
    journeyId: asId(payload.journeyId),
    budgetId: asId(payload.budgetId),
    splitCalculationId: asId(payload.splitCalculationId),
    calculationVersion: Number.isFinite(Number(payload.calculationVersion))
      ? Number(payload.calculationVersion)
      : null,
    calculationStatus: String(payload.calculationStatus || ''),
    calculationStatusLabel: calculationStatusLabel(payload.calculationStatus),
    baseCurrency: currencyCode,
    totalActiveExpenseAmount: total,
    totalLabel: formatExpenseCardAmount(total, currencyCode),
    participantCount: Number.isFinite(Number(payload.participantCount))
      ? Number(payload.participantCount)
      : members.length,
    remainingMemberCount: Number.isFinite(Number(payload.remainingMemberCount))
      ? Number(payload.remainingMemberCount)
      : 0,
    receivableCount: Number.isFinite(Number(payload.receivableCount)) ? Number(payload.receivableCount) : null,
    payableCount: Number.isFinite(Number(payload.payableCount)) ? Number(payload.payableCount) : null,
    balancedCount: Number.isFinite(Number(payload.balancedCount)) ? Number(payload.balancedCount) : null,
    calculatedAt: payload.calculatedAt || '',
    members,
    webRoute: balanceWebRoute(payload),
  };
}

export function memoryWebRoute(payload) {
  const journeyId = asId(payload?.journeyId);
  const memoryEntryId = asId(payload?.memoryEntryId);
  if (!isSnowflakeId(journeyId) || !isSnowflakeId(memoryEntryId)) return '';
  return `/journeys/${journeyId}/journal?memory=${memoryEntryId}`;
}

export function memoryTypeCardLabel(type) {
  const key = String(type || '');
  const labels = {
    JOURNAL: '日記',
    MOOD: '心情',
    PHOTO_NOTE: '照片筆記',
    PLACE_NOTE: '地點筆記',
    FOOD_NOTE: '美食筆記',
    HIGHLIGHT: '亮點',
  };
  return labels[key] || '';
}

export function formatMemoryDayContext(payload) {
  const date = String(payload?.dayDateSnapshot || '').trim();
  const title = String(payload?.dayTitleSnapshot || '').trim();
  if (date && title) return `${date} · ${title}`;
  return date || title;
}

export function normalizeMemoryBusinessCard(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const payload = raw.payload && typeof raw.payload === 'object' ? raw.payload : {};
  return {
    rendererKey: String(raw.rendererKey || ''),
    cardSchemaVersion: raw.cardSchemaVersion == null ? null : Number(raw.cardSchemaVersion),
    fallbackText: String(raw.fallbackText || '').trim(),
    payload,
    memoryEntryId: asId(payload.memoryEntryId),
    journeyId: asId(payload.journeyId),
    title: String(payload.title || raw.fallbackText || '').trim(),
    contentPreview: String(payload.contentPreview || '').trim(),
    occurredAt: payload.occurredAt || '',
    memoryType: String(payload.memoryType || ''),
    memoryTypeLabel: memoryTypeCardLabel(payload.memoryType),
    dayContext: formatMemoryDayContext(payload),
    activityTitle: String(payload.activityTitleSnapshot || '').trim(),
    coverObjectKey: String(payload.coverObjectKey || '').trim(),
    webRoute: memoryWebRoute(payload),
  };
}

export function normalizeSituationCard(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    ...raw,
    cardSchemaVersion: raw.cardSchemaVersion == null ? null : Number(raw.cardSchemaVersion),
    situationId: asId(raw.situationId),
    journeyId: asId(raw.journeyId),
    assignedToMemberId: raw.assignedToMemberId == null ? '' : asId(raw.assignedToMemberId),
    title: String(raw.title || '').trim(),
    situationType: String(raw.situationType || ''),
    severity: String(raw.severity || ''),
    situationStatus: String(raw.situationStatus || ''),
    deepLink: String(raw.deepLink || ''),
    situationTypeLabel: raw.situationType ? situationTypeLabel(raw.situationType) : '',
    severityLabel: raw.severity ? situationSeverityLabel(raw.severity) : '',
    situationStatusLabel: situationStatusLabel(raw.situationStatus),
    webRoute: situationWebRoute({
      journeyId: asId(raw.journeyId),
      situationId: asId(raw.situationId),
    }),
  };
}
